import { Injectable, inject } from '@angular/core';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { SsApiService } from './api/ss-api.service';
import { UserService } from './user/user.service';
import { PlatformService } from './platform.service';
import { SsWebSocketsService } from '../vendor/ss-web-sockets/ss-web-sockets.service';
import { SsWebSocketsPrivateChanel, SsWebSocketsPublicChanel } from '../vendor/ss-web-sockets/ss-web-sockets.types';
import {
  BonusesWSType,
  FreespinsWSType,
  GameLimitWSType,
  LootboxesWSType,
  PaymentsWSType, TournamentsStartWSType, TournamentsStatusItemWS,
} from '../types/notifications-type';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private _api = inject(SsApiService);
  private _user = inject(UserService);
  private _platform = inject(PlatformService);
  private _ssSockets = inject(SsWebSocketsService);

  /**
   * Socket channels
   */
  private _winners: Subject<any> = new Subject<any>();
  private _bonuses: Subject<any> = new Subject<any>();
  private _freespins: Subject<any> = new Subject<any>();
  private _compPoints: Subject<any> = new Subject<any>();
  private _groups: Subject<any> = new Subject<any>();
  private _payments: Subject<any> = new Subject<any>();
  private _lootboxes: Subject<any> = new Subject<any>();
  private _tournaments: Subject<any> = new Subject<any>();
  private _tournamentsStarted: Subject<any> = new Subject<any>();
  private _limits: Subject<any> = new Subject<any>();
  private _analytics: Subject<any> = new Subject<any>();
  private _jackpots: Subject<any> = new Subject<any>();
  private _jackpotsWin: Subject<any> = new Subject<any>();
  private _userBalance: Subject<any> = new Subject<any>();

  /**
   * Protected access to channels
   */
  public winners$: Observable<any> = this._winners.asObservable();
  public bonuses$: Observable<BonusesWSType> = this._bonuses.asObservable();
  public freespins$: Observable<FreespinsWSType> = this._freespins.asObservable();
  public compPoints$: Observable<any> = this._compPoints.asObservable();
  public groups$: Observable<any> = this._groups.asObservable();
  public payments$: Observable<PaymentsWSType> = this._payments.asObservable();
  public lootboxes$: Observable<LootboxesWSType> = this._lootboxes.asObservable();
  public tournaments$: Observable<TournamentsStatusItemWS> = this._tournaments.asObservable();
  public tournamentsStarted$: Observable<TournamentsStartWSType> = this._tournamentsStarted.asObservable();
  public limits$: Observable<GameLimitWSType> = this._limits.asObservable();
  public analytics$: Observable<any> = this._analytics.asObservable();
  public jackpots$: Observable<any> = this._jackpots.asObservable();
  public jackpotsWin$: Observable<any> = this._jackpotsWin.asObservable();
  public userBalance$: Observable<any> = this._userBalance.asObservable();

  private _channels = [
    {channel: SsWebSocketsPublicChanel.LATEST_WINNERS, channel$: this._winners},
    {channel: SsWebSocketsPrivateChanel.BONUSES_CHANGED, channel$: this._bonuses},
    {channel: SsWebSocketsPrivateChanel.FREESPINS_CHANGED, channel$: this._freespins},
    {channel: SsWebSocketsPrivateChanel.COMP_POINTS, channel$: this._compPoints},
    {channel: SsWebSocketsPrivateChanel.GROUPS_UPDATES, channel$: this._groups},
    {channel: SsWebSocketsPrivateChanel.PAYMENTS_CHANGED, channel$: this._payments},
    {channel: SsWebSocketsPrivateChanel.LOOTBOXES_CHANGED, channel$: this._lootboxes},
    {channel: SsWebSocketsPrivateChanel.TOURNAMENTS_STATUSES, channel$: this._tournaments},
    {channel: SsWebSocketsPrivateChanel.TOURNAMENTS_STARTED, channel$: this._tournamentsStarted},
    {channel: SsWebSocketsPrivateChanel.GAME_LIMITS, channel$: this._limits},
    {channel: SsWebSocketsPrivateChanel.ANALYTICS, channel$: this._analytics},
    {channel: SsWebSocketsPublicChanel.JACKPOTS, channel$: this._jackpots},
    {channel: SsWebSocketsPrivateChanel.JACKPOT_WIN_AWARD, channel$: this._jackpotsWin},
    {channel: SsWebSocketsPrivateChanel.USER_BALANCE, channel$: this._userBalance}
  ];

  public createConnection() {
    this._user.auth$.pipe(
      filter(() => this._platform.isBrowser),
      tap(() => this._ssSockets.disconnect()),
      switchMap(() => {
        return this._api.playerSettings().pipe(
          map((res: any) => res.cent),
          tap((cent: any) => {
            this._ssSockets.init({
              url: cent.url,
              token: cent.token,
              timestamp: cent.timestamp,
              user: of(cent.user) as ReplaySubject<any>
            });
          }),
          switchMap(() => {
            return of(this._channels).pipe(
              map((channel: { channel: SsWebSocketsPublicChanel | SsWebSocketsPrivateChanel, channel$: Subject<any>}[]) =>
                channel.map(item => {
                  return this._ssSockets.connect(item.channel).pipe(
                    map(data => ({
                      ...data,
                      notify_channel: item.channel
                    }))).subscribe(item.channel$);
                }),
              )
            );
          })
        );
      })
    ).subscribe();
  }
}
